// Shared thumbs up/down feedback for the Stimulus Remix AI chats (playbook,
// experiment). Renders a feedback row under an assistant reply and posts the
// rating + conversation to the AI chat feedback endpoint. Mirrors the builder
// chat's React MessageFeedback component.

const THUMBS_UP_ICON =
  '<svg width="12" height="12" viewBox="0 0 512 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M313.4 32.9c26 5.2 42.9 30.5 37.7 56.5l-2.3 11.4c-5.3 26.7-15.1 52.1-28.8 75.2H464c26.5 0 48 21.5 48 48 0 18.5-10.5 34.6-25.9 42.6C497 312 504 326.2 504 342c0 23.4-15 43.3-35.9 50.6 5 8.2 7.9 17.8 7.9 28 0 24.1-16.1 44.5-38.1 50.9 .6 3.9 .9 7.7 .9 11.6 0 26.5-21.5 48-48 48H294.5c-19 0-37.5-5.6-53.3-16.1l-38.5-25.7C176 462.4 160 435.5 160 407.4V320 249.3l0-.7c0-21.2 8.4-41.5 23.4-56.5l52.1-52.1c14-14 21.9-33 21.9-52.8V72c0-22.1 17.9-40 40-40 4.6 0 9.1 .5 13.4 1.4zM32 192H96c17.7 0 32 14.3 32 32V448c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32V224c0-17.7 14.3-32 32-32z"/></svg>';

const THUMBS_DOWN_ICON =
  '<svg width="12" height="12" viewBox="0 0 512 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M313.4 479.1c26-5.2 42.9-30.5 37.7-56.5l-2.3-11.4c-5.3-26.7-15.1-52.1-28.8-75.2H464c26.5 0 48-21.5 48-48 0-18.5-10.5-34.6-25.9-42.6C497 200 504 185.8 504 170c0-23.4-15-43.3-35.9-50.6 5-8.2 7.9-17.8 7.9-28 0-24.1-16.1-44.5-38.1-50.9 .6-3.9 .9-7.7 .9-11.6 0-26.5-21.5-48-48-48H294.5c-19 0-37.5 5.6-53.3 16.1L202.7 22.7C176 49.6 160 76.5 160 104.6V192v70.7l0 .7c0 21.2 8.4 41.5 23.4 56.5l52.1 52.1c14 14 21.9 33 21.9 52.8V440c0 22.1 17.9 40 40 40 4.6 0 9.1-.5 13.4-1.4zM32 320H96c17.7 0 32-14.3 32-32V64c0-17.7-14.3-32-32-32H32C14.3 32 0 46.3 0 64V288c0 17.7 14.3 32 32 32z"/></svg>';

const BUTTON_CLASS =
  "p-0 bg-transparent border-0 leading-none cursor-pointer text-slate-300 hover:text-slate-500 transition-colors";

const SUPPORT_LINK_CLASS =
  "p-0 bg-transparent border-0 text-sm text-blue-600 hover:underline cursor-pointer";

const PREFILL_SECTION_MAX_LENGTH = 400;
const SUPPORT_PREFILL_INTRO = "My AI chat didn't work as expected.";

function stripHtml(text) {
  const element = document.createElement("div");
  element.innerHTML = String(text);
  return element.textContent || element.innerText || "";
}

function normalizeForPrefill(text, maxLength = PREFILL_SECTION_MAX_LENGTH) {
  const plain = stripHtml(text).trim().replace(/\s+/g, " ");
  if (!plain) return "";
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength - 3)}...`;
}

// Builds an Intercom prefill from the most recent user/assistant exchange.
export function buildSupportChatPrefill(messages, fallback) {
  const history = (Array.isArray(messages) ? messages : [])
    .filter(
      (message) => message?.role === "user" || message?.role === "assistant"
    )
    .map((message) => ({
      role: message.role,
      content: normalizeForPrefill(message.content),
    }))
    .filter((message) => message.content);

  const lastAssistant = [...history]
    .reverse()
    .find((message) => message.role === "assistant");
  const lastUser = [...history]
    .reverse()
    .find((message) => message.role === "user");

  if (!lastUser && !lastAssistant) {
    return fallback || SUPPORT_PREFILL_INTRO;
  }

  const parts = [SUPPORT_PREFILL_INTRO];
  if (lastUser) parts.push(`My request:\n${lastUser.content}`);
  if (lastAssistant) parts.push(`AI response:\n${lastAssistant.content}`);
  return parts.join("\n\n");
}

// Opens Intercom with a default prefill when no Stimulus controller is available.
export function openSupportChat(message) {
  if (typeof window.Intercom !== "function") return;

  window.Intercom("showNewMessage", message || SUPPORT_PREFILL_INTRO);
}

function appendFeedbackSupportLink(row, messages) {
  if (typeof window.Intercom !== "function") return;

  const separator = document.createElement("span");
  separator.className = "text-sm text-slate-300";
  separator.textContent = "·";
  row.appendChild(separator);

  const prefill = buildSupportChatPrefill(messages);
  const link = document.createElement("button");
  link.type = "button";
  link.className = `${SUPPORT_LINK_CLASS} whitespace-nowrap`;
  link.textContent = "Get support →";
  link.addEventListener("click", () => openSupportChat(prefill));
  row.appendChild(link);
}

// HTML for the feedback row; buttons dispatch to `${controllerIdentifier}#submitFeedback`
export function feedbackRowHtml(controllerIdentifier) {
  return `
    <div class="flex flex-nowrap items-center gap-2 mt-1 pl-4 text-sm" data-chat-feedback>
      <button type="button" title="Good response" data-rating="up" data-action="click->${controllerIdentifier}#submitFeedback" class="${BUTTON_CLASS}">${THUMBS_UP_ICON}</button>
      <button type="button" title="Bad response" data-rating="down" data-action="click->${controllerIdentifier}#submitFeedback" class="${BUTTON_CLASS}">${THUMBS_DOWN_ICON}</button>
    </div>
  `;
}

// Handles a thumbs click: locks the row, acknowledges, and posts the feedback.
export async function submitChatFeedback(event, { source, messages }) {
  const button = event.currentTarget;
  const row = button.closest("[data-chat-feedback]");
  if (!row || row.dataset.submitted === "true") return;

  const rating = button.dataset.rating;
  row.dataset.submitted = "true";

  row.querySelectorAll("button").forEach((element) => {
    element.disabled = true;
    element.classList.remove("hover:text-slate-500", "cursor-pointer");
    element.classList.add("cursor-default");
    if (element.dataset.rating === rating) {
      element.classList.remove("text-slate-300");
      element.classList.add("text-black");
    }
  });

  const thanks = document.createElement("span");
  thanks.className = "text-sm text-slate-400 whitespace-nowrap";
  thanks.textContent = "Thanks!";
  row.appendChild(thanks);
  appendFeedbackSupportLink(row, messages);

  try {
    await fetch("/ai_chat_feedbacks", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.content || "",
        Accept: "application/json",
      },
      body: JSON.stringify({
        source,
        link: window.location.href,
        rating,
        messages,
      }),
    });
  } catch (error) {
    // best-effort; leave the acknowledgement in place
  }
}
